import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useReducer,
} from 'react';

import {ACTIONS} from './constants';
import reducer, {initialState, Change, Changes, ID} from './reducer';

type ChangeContextType = {
	state: typeof initialState;

	add(primary: ID, field: string): void;
	get(primary: ID): Change;
	changed(primary: ID, field: string): boolean;
	remove(primary: ID, field: string): void;
	reset(changes?: Changes): void;
};

const StoreContext = createContext<ChangeContextType>({} as any);

export const useChangeStore = () => useContext<ChangeContextType>(StoreContext);

export const ChangeStoreProvider = ({children, gridEditor}: any) => {
	const [state, dispatch] = useReducer(reducer, initialState);

	// When update changes updates, update the count
	useEffect(() => {
		const changes = state.changes;
		let count = 0;
		for (const key in changes) count += changes[key].length;
		dispatch({
			type: ACTIONS.SET_COUNT,
			count,
		});
	}, [state.changes]);

	const reset = useCallback((changes?: Changes) => {
		dispatch({type: ACTIONS.RESET, changes});
	}, []);

	const get = useCallback(
		(primary: ID) => state.changes[primary] || [],
		[state.changes],
	);

	const changed = useCallback(
		(primary: ID, field: string) => get(primary).includes(field),
		[get],
	);

	const addFieldTimeout = React.useRef<NodeJS.Timeout>();
	const addField = useCallback(() => {
		const toAdd: Changes = {};
		const reset = () => {
			addFieldTimeout.current && clearTimeout(addFieldTimeout.current);
			addFieldTimeout.current = setTimeout(() => {
				dispatch({
					type: ACTIONS.ADD_CHANGES,
					changes: toAdd,
				});
			}, 25);
		};
		return (primary: ID, field: string) => {
			reset();
			!toAdd[primary] ? (toAdd[primary] = [field]) : toAdd[primary].push(field);
		};
	}, []);

	const removeFieldTimeout = React.useRef<NodeJS.Timeout>();
	const removeField = useCallback(() => {
		const toRemove: Changes = {};
		const reset = () => {
			removeFieldTimeout.current && clearTimeout(removeFieldTimeout.current);
			removeFieldTimeout.current = setTimeout(() => {
				dispatch({
					type: ACTIONS.REMOVE_CHANGES,
					changes: toRemove,
				});
			}, 25);
		};
		return (primary: ID, field: string) => {
			reset();
			!toRemove[primary]
				? (toRemove[primary] = [field])
				: toRemove[primary].push(field);
		};
	}, []);

	return (
		<StoreContext.Provider
			value={{
				state,
				add: addField(),
				changed,
				get,
				remove: removeField(),
				reset,
			}}
		>
			{children}
		</StoreContext.Provider>
	);
};
